/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * Pure IAM.Voice catalog lookup shared by React and non-React application
 * layers. It resolves the persisted RU/EN locale, performs safe named-value
 * interpolation, and falls back to English before exposing a missing key.
 * Rendering and locale controls are intentionally outside this module.
 */

import en from './en.json';
import ru from './ru.json';

export type Locale = 'ru' | 'en';
export type TranslationParams = Record<string, string | number>;
type Catalog = Record<string, string>;

export const LOCALE_STORAGE_KEY = 'iam-voice-locale';
const catalogs: Record<Locale, Catalog> = { en, ru };

/** Resolves a persisted locale while remaining safe during SSR and tests. */
export function resolveInitialLocale(): Locale {
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY) === 'en' ? 'en' : 'ru';
  } catch {
    return 'ru';
  }
}

/** Interpolates named placeholders without evaluating catalog content. */
function interpolate(raw: string, params?: TranslationParams): string {
  if (!params) return raw;
  return Object.entries(params).reduce(
    (value, [key, replacement]) => value.split(`{${key}}`).join(String(replacement)),
    raw
  );
}

/** Looks up and interpolates one key for an explicit locale. */
export function translate(locale: Locale, key: string, params?: TranslationParams): string {
  return interpolate(catalogs[locale][key] ?? catalogs.en[key] ?? key, params);
}

/** Translates non-React messages using the same persisted locale as the UI. */
export function translateCurrent(key: string, params?: TranslationParams): string {
  return translate(resolveInitialLocale(), key, params);
}
